import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  GlobeAltIcon,
  ChevronRightIcon,
  CubeIcon,
  BuildingOfficeIcon,
  EyeIcon,
  PlusIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

interface DashboardStats {
  totalBookings: number;
  totalRevenue: number;
  totalPackages: number;
  totalResorts: number;
}

interface RecentBooking {
  _id: string;
  bookingCode: string;
  customer: {
    firstName: string;
    lastName: string;
  };
  package?: {
    name: string;
  } | null;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
}

interface PopularPackage {
  _id: string;
  name: string;
  totalBookings: number;
  totalRevenue: number;
  currency: string;
  packageCode?: string;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({ totalBookings: 0, totalRevenue: 0, totalPackages: 0, totalResorts: 0 });
  const [recentBookings, setRecentBookings] = useState<RecentBooking[]>([]);
  const [popularPackages, setPopularPackages] = useState<PopularPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      // Fetch booking statistics
      const [bookingStats, packagesRes, resortsRes, bookingsRes, popularPackagesRes] = await Promise.all([
        axios.get(`${baseURL}/api/bookings/stats/overview`, { headers }),
        axios.get(`${baseURL}/api/packages`, { headers }),
        axios.get(`${baseURL}/api/resorts`, { headers }),
        axios.get(`${baseURL}/api/bookings?limit=4&sort=-createdAt`, { headers }),
        axios.get(`${baseURL}/api/packages/analytics/popular`, { headers })
      ]);

      setStats({
        totalBookings: bookingStats.data.totalBookings || 0,
        totalRevenue: bookingStats.data.totalRevenue || 0,
        totalPackages: packagesRes.data.packages?.length || 0,
        totalResorts: resortsRes.data.resorts?.length || 0
      });

      if (bookingsRes.data.bookings) {
        setRecentBookings(bookingsRes.data.bookings);
      }

      if (popularPackagesRes.data.popularPackages) {
        setPopularPackages(popularPackagesRes.data.popularPackages);
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
      // Keep fallback mock data for display purposes
      setStats({ totalBookings: 0, totalRevenue: 0, totalPackages: 0, totalResorts: 0 });
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    {
      name: 'Total Bookings',
      value: loading ? '...' : stats.totalBookings.toLocaleString(),
      change: '+12%',
      changeType: 'increase',
      icon: DocumentTextIcon,
      color: 'bg-blue-500'
    },
    {
      name: 'Total Revenue',
      value: loading ? '...' : `$${stats.totalRevenue.toLocaleString()}`,
      change: '+8.2%',
      changeType: 'increase',
      icon: CurrencyDollarIcon,
      color: 'bg-green-500'
    },
    {
      name: 'Active Packages',
      value: loading ? '...' : stats.totalPackages.toString(),
      change: '+2',
      changeType: 'increase',
      icon: CubeIcon,
      color: 'bg-purple-500'
    },
    {
      name: 'Partner Resorts',
      value: loading ? '...' : stats.totalResorts.toString(),
      change: '+5',
      changeType: 'increase',
      icon: BuildingOfficeIcon,
      color: 'bg-orange-500'
    }
  ];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    const symbol = currency === 'AUD' ? 'A$' : '$';
    return `${symbol}${amount.toLocaleString()}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <Helmet>
        <title>Dashboard - Bali Getaway Admin</title>
      </Helmet>

      <div className="space-y-8 pb-8">

        {/* Modern Header with Gradient Background */}
        <div className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 rounded-3xl shadow-2xl overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-yellow-300/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '2s' }}></div>
            <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-pink-300/15 rounded-full blur-xl animate-pulse" style={{ animationDelay: '4s' }}></div>
          </div>

          <div className="relative p-8 lg:p-12">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <ChartBarIcon className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold text-white mb-2">
                      Welcome back, {user?.firstName}! 👋
                    </h1>
                    <p className="text-blue-100 text-lg">
                      Here's your business overview for today
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-6 text-white/90">
                  <div className="flex items-center space-x-2">
                    <CalendarDaysIcon className="h-5 w-5" />
                    <span className="font-medium">
                      {new Date().toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <GlobeAltIcon className="h-5 w-5" />
                    <span>Bali Operations</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/packages"
                  className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-2xl font-semibold hover:bg-white/30 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  title="View public packages page"
                >
                  <EyeIcon className="mr-2 h-5 w-5" />
                  View Public Site
                </Link>
                <button
                  onClick={() => navigate('/admin/packages/create')}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-2xl font-semibold hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <PlusIcon className="mr-2 h-5 w-5" />
                  Quick Add Package
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center">
            <PlusIcon className="h-5 w-5 text-orange-500 mr-2" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <button
              onClick={() => navigate('/admin/packages/create')}
              className="flex flex-col items-center justify-center p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl hover:shadow-md transition-all duration-300 border border-blue-100 hover:border-blue-300 hover:scale-105 group"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mb-3 shadow group-hover:shadow-lg">
                <CubeIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-800 group-hover:text-blue-700">Add Package</span>
            </button>

            <button
              onClick={() => navigate('/admin/resorts/create')}
              className="flex flex-col items-center justify-center p-5 bg-gradient-to-br from-green-50 to-teal-50 rounded-xl hover:shadow-md transition-all duration-300 border border-green-100 hover:border-green-300 hover:scale-105 group"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center mb-3 shadow group-hover:shadow-lg">
                <BuildingOfficeIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-800 group-hover:text-green-700">Add Resort</span>
            </button>

            <button
              onClick={() => navigate('/admin/destinations')}
              className="flex flex-col items-center justify-center p-5 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl hover:shadow-md transition-all duration-300 border border-purple-100 hover:border-purple-300 hover:scale-105 group"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-3 shadow group-hover:shadow-lg">
                <MapPinIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-800 group-hover:text-purple-700">Destinations</span>
            </button>

            <a
              href="/packages"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center p-5 bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl hover:shadow-md transition-all duration-300 border border-orange-100 hover:border-orange-300 hover:scale-105 group"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center mb-3 shadow group-hover:shadow-lg">
                <EyeIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-800 group-hover:text-orange-700">View Site</span>
            </a>

            <button
              onClick={() => navigate('/admin/bookings')}
              className="flex flex-col items-center justify-center p-5 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl hover:shadow-md transition-all duration-300 border border-red-100 hover:border-red-300 hover:scale-105 group"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-rose-500 rounded-full flex items-center justify-center mb-3 shadow group-hover:shadow-lg">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-800 group-hover:text-red-700">Bookings</span>
            </button>

            <button
              onClick={() => navigate('/admin/analytics')}
              className="flex flex-col items-center justify-center p-5 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl hover:shadow-md transition-all duration-300 border border-cyan-100 hover:border-cyan-300 hover:scale-105 group"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mb-3 shadow group-hover:shadow-lg">
                <ChartBarIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-800 group-hover:text-cyan-700">Analytics</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
            <button
              onClick={fetchDashboardData}
              className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Modern Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.map((stat, index) => (
            <div
              key={stat.name}
              className="group relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 hover:border-blue-200 transform hover:-translate-y-2"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Gradient Background Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {/* Content */}
              <div className="relative p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`${stat.color} rounded-2xl p-4 shadow-lg group-hover:shadow-xl transform group-hover:rotate-6 transition-all duration-300`}>
                    <stat.icon className="h-7 w-7 text-white" />
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${stat.changeType === 'increase' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                    {stat.change}
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                    {stat.name}
                  </p>
                  <p className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    {stat.value}
                  </p>
                </div>

                {/* Decorative Element */}
                <div className="absolute top-4 right-4 w-2 h-2 bg-blue-400 rounded-full opacity-30 group-hover:opacity-60 transition-opacity" />
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Modern Recent Bookings */}
          <div className="group relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-blue-200">
            {/* Gradient Border Effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur"></div>

            <div className="relative">
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <DocumentTextIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        Recent Bookings
                      </h2>
                      <p className="text-gray-600">Latest customer reservations</p>
                    </div>
                  </div>
                  <button className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-2xl hover:bg-blue-100 transition-colors font-medium">
                    View All
                    <ChevronRightIcon className="ml-1 h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="p-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                    <span className="text-gray-600 font-medium">Loading bookings...</span>
                  </div>
                ) : recentBookings.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">No recent bookings found</p>
                    <p className="text-gray-400 text-sm mt-1">New bookings will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recentBookings.map((booking, index) => (
                      <div
                        key={booking._id}
                        className="group p-4 bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-2xl hover:from-blue-50 hover:to-purple-50 transition-all duration-300 border border-gray-100 hover:border-blue-200 hover:shadow-lg"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-xl flex items-center justify-center text-white font-bold shadow-lg group-hover:shadow-xl transform group-hover:scale-110 transition-all duration-300">
                              {booking.customer.firstName.charAt(0)}{booking.customer.lastName.charAt(0)}
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                                {booking.customer.firstName} {booking.customer.lastName}
                              </p>
                              <p className="text-sm text-gray-500 font-medium">{booking.bookingCode}</p>
                              <p className="text-sm text-gray-600 mt-1">{booking.package?.name || 'Manual Payment'}</p>
                            </div>
                          </div>
                          <div className="text-right space-y-2">
                            <p className="font-bold text-lg text-gray-900">
                              {formatAmount(booking.amount, booking.currency)}
                            </p>
                            <div className="flex items-center space-x-3">
                              <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${getStatusColor(booking.status)}`}>
                                {booking.status.replace('_', ' ').toUpperCase()}
                              </span>
                              <span className="text-xs text-gray-500 font-medium">
                                {formatDate(booking.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Modern Popular Packages */}
          <div className="group relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-purple-200">
            {/* Gradient Border Effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur"></div>

            <div className="relative">
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <ArrowTrendingUpIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                        Popular Packages
                      </h2>
                      <p className="text-gray-600">Top performing vacation packages</p>
                    </div>
                  </div>
                  <button className="inline-flex items-center px-4 py-2 bg-purple-50 text-purple-600 rounded-2xl hover:bg-purple-100 transition-colors font-medium">
                    View Analytics
                    <ChevronRightIcon className="ml-1 h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                      <span className="text-gray-500">Loading popular packages...</span>
                    </div>
                  ) : popularPackages.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No package analytics available yet
                    </div>
                  ) : (
                    popularPackages.map((pkg, index) => (
                      <div
                        key={pkg._id}
                        className="group p-4 bg-gradient-to-r from-gray-50 to-purple-50/30 rounded-2xl hover:from-purple-50 hover:to-pink-50 transition-all duration-300 border border-gray-100 hover:border-purple-200 hover:shadow-lg"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-400 rounded-xl flex items-center justify-center text-white font-bold shadow-lg group-hover:shadow-xl transform group-hover:scale-110 transition-all duration-300">
                              #{index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-gray-900 group-hover:text-purple-700 transition-colors">
                                {pkg.name}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">{pkg.totalBookings} bookings</p>
                              <p className="text-xs text-gray-500 font-medium">
                                {pkg.packageCode || 'Package Code N/A'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                              {formatAmount(pkg.totalRevenue, pkg.currency)}
                            </p>
                            <div className="flex items-center justify-end mt-2">
                              <BanknotesIcon className="h-4 w-4 text-green-500 mr-1" />
                              <span className="text-xs text-green-600 font-bold">Revenue</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comprehensive System Status - Separate Full Width Section */}
        <div className="w-screen bg-white shadow-xl border-t border-b border-gray-200 py-8 -ml-64 pl-72 pr-8 mt-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                <GlobeAltIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">System Health & Performance</h2>
                <p className="text-gray-600">Real-time monitoring of all services and infrastructure</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-green-600">All Systems Operational</span>
            </div>
          </div>

          {/* Full Width Grid - 6 columns on large screens */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6 mb-8">
            {/* Core Services */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Core Services</h3>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Database</span>
                  <span className="text-sm font-semibold text-green-600">99.9%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">API Server</span>
                  <span className="text-sm font-semibold text-green-600">100%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">CDN</span>
                  <span className="text-sm font-semibold text-green-600">99.8%</span>
                </div>
              </div>
            </div>

            {/* Payment & Integration */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Payments</h3>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Flywire Gateway</span>
                  <span className="text-sm font-semibold text-green-600">Online</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">LeadSquare CRM</span>
                  <span className="text-sm font-semibold text-yellow-600">Monitoring</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Email Service</span>
                  <span className="text-sm font-semibold text-green-600">Active</span>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Performance</h3>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Avg Response</span>
                  <span className="text-sm font-semibold text-blue-600">142ms</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Server Load</span>
                  <span className="text-sm font-semibold text-blue-600">23%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Uptime</span>
                  <span className="text-sm font-semibold text-green-600">99.9%</span>
                </div>
              </div>
            </div>

            {/* Traffic & Usage */}
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6 border border-orange-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Traffic</h3>
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Active Users</span>
                  <span className="text-sm font-semibold text-orange-600">247</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Requests/min</span>
                  <span className="text-sm font-semibold text-orange-600">1.2k</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Error Rate</span>
                  <span className="text-sm font-semibold text-green-600">0.1%</span>
                </div>
              </div>
            </div>

            {/* Security & Monitoring */}
            <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 border border-red-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Security</h3>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">SSL Status</span>
                  <span className="text-sm font-semibold text-green-600">Active</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Firewall</span>
                  <span className="text-sm font-semibold text-green-600">Protected</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Auth Rate</span>
                  <span className="text-sm font-semibold text-green-600">99.2%</span>
                </div>
              </div>
            </div>

            {/* Storage & Backup */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Storage</h3>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Disk Usage</span>
                  <span className="text-sm font-semibold text-blue-600">68%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Backup Status</span>
                  <span className="text-sm font-semibold text-green-600">Current</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">CDN Cache</span>
                  <span className="text-sm font-semibold text-blue-600">92%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity & Alerts - Full Width */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <ChartBarIcon className="h-5 w-5 text-blue-500 mr-2" />
                Recent System Activity
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Database backup completed</p>
                    <p className="text-xs text-gray-500">2 minutes ago</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">SSL certificate renewed</p>
                    <p className="text-xs text-gray-500">1 hour ago</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">High traffic detected</p>
                    <p className="text-xs text-gray-500">3 hours ago</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <DocumentTextIcon className="h-5 w-5 text-orange-500 mr-2" />
                Content Statistics
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                  <p className="text-2xl font-bold text-blue-600">{stats.totalPackages}</p>
                  <p className="text-sm text-gray-600">Active Packages</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
                  <p className="text-2xl font-bold text-green-600">{stats.totalResorts}</p>
                  <p className="text-sm text-gray-600">Partner Resorts</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                  <p className="text-2xl font-bold text-purple-600">{stats.totalBookings}</p>
                  <p className="text-sm text-gray-600">Total Bookings</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-100">
                  <p className="text-2xl font-bold text-orange-600">$</p>
                  <p className="text-sm text-gray-600">Revenue</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
